import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChimeService } from '../chime.service';
import { DeviceSelectorComponent } from '../device-selector/device-selector.component';
import { SelectComponent, SelectOption } from '../../ui/select/select.component';

@Component({
  selector: 'app-call-devices',
  standalone: true,
  imports: [
    DeviceSelectorComponent,
    SelectComponent,
    FormsModule,
    MatButtonModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="custom-context-menu-wrapper no-subscript d-flex flex-column gap-3 p-3 mb-2 w-[280px]">
      <!-- Title -->
      <div class="font-bold text-slate-800 text-sm border-b pb-1 mb-1">
        {{ 'MediaDevicePicker.Title' | translate }}
      </div>

      <app-device-selector
        [kind]="'videoinput'"
        [label]="'DeviceSelector.CameraLabel'"
        [devices]="chimeService.videoInputDevices()"
        [selectedId]="chimeService.selectedVideoInputId()"
        (selectionChange)="onCameraSelect($event)"
      />
      <app-device-selector
        [kind]="'audioinput'"
        [label]="'DeviceSelector.MicrophoneLabel'"
        [devices]="chimeService.audioInputDevices()"
        [selectedId]="chimeService.selectedAudioInputId()"
        (selectionChange)="onMicrophoneSelect($event)"
      />
      <app-device-selector
        [kind]="'audiooutput'"
        [label]="'DeviceSelector.SpeakerLabel'"
        [devices]="chimeService.audioOutputDevices()"
        [selectedId]="chimeService.selectedAudioOutputId()"
        (selectionChange)="onSpeakerSelect($event)"
      />

      <div class="border-t my-1 border-slate-200"></div>

      <!-- Audio Quality Profile -->
      <div class="w-full text-left">
        <label class="form-label mb-1 block text-xs font-semibold text-slate-600">{{ 'VideoCall.AudioQuality.Label' | translate }}</label>
        <ui-select
          class="w-full"
          [options]="audioQualityOptions()"
          [ngModel]="chimeService.audioQuality()"
          (valueChange)="onAudioQualityChange($any($event))"
          [compact]="true"
        ></ui-select>
      </div>

      <!-- Video Quality Profile -->
      <div class="w-full text-left">
        <label class="form-label mb-1 block text-xs font-semibold text-slate-600">{{ 'VideoCall.VideoQuality.Label' | translate }}</label>
        <ui-select
          class="w-full"
          [options]="videoQualityOptions()"
          [ngModel]="chimeService.videoQuality()"
          (valueChange)="onVideoQualityChange($any($event))"
          [compact]="true"
        ></ui-select>
      </div>

      <div class="border-t my-1 border-slate-200"></div>

      <!-- Advanced Audio settings title -->
      <div class="text-xs font-semibold text-slate-600 mb-1">
        {{ 'VideoCall.AudioProcessing.Title' | translate }}
      </div>

      <!-- Advanced Audio Toggles -->
      <div class="d-flex flex-column gap-2">
        <mat-slide-toggle
          [checked]="chimeService.echoCancellation()"
          (change)="onEchoCancellationChange($event.checked)"
          color="primary"
          class="text-xs">
          {{ 'VideoCall.AudioProcessing.EchoCancellation' | translate }}
        </mat-slide-toggle>

        <mat-slide-toggle
          [checked]="chimeService.noiseSuppression()"
          (change)="onNoiseSuppressionChange($event.checked)"
          color="primary"
          class="text-xs">
          {{ 'VideoCall.AudioProcessing.NoiseSuppression' | translate }}
        </mat-slide-toggle>

        <mat-slide-toggle
          [checked]="chimeService.autoGainControl()"
          (change)="onAutoGainControlChange($event.checked)"
          color="primary"
          class="text-xs">
          {{ 'VideoCall.AudioProcessing.AutoGainControl' | translate }}
        </mat-slide-toggle>
      </div>

      <div class="border-t my-1 border-slate-200"></div>

      <!-- Advanced Video Effects / Blur -->
      <div class="text-xs font-semibold text-slate-600 mb-1">
        {{ 'VideoCall.VideoEffects.Title' | translate }}
      </div>

      <div class="d-flex flex-column gap-2">
        <mat-slide-toggle
          [checked]="chimeService.isBlurEnabled()"
          (change)="onBlurToggleChange($event.checked)"
          color="primary"
          class="text-xs">
          {{ 'VideoCall.VideoEffects.BackgroundBlur' | translate }}
        </mat-slide-toggle>

        @if (chimeService.isBlurEnabled()) {
          <div class="w-full text-left mt-1">
            <label class="form-label mb-1 block text-xs font-semibold text-slate-600">{{ 'VideoCall.BlurStrength.Label' | translate }}</label>
            <ui-select
              class="w-full"
              [options]="blurStrengthOptions()"
              [ngModel]="chimeService.blurStrength()"
              (valueChange)="onBlurStrengthChange($any($event))"
              [compact]="true"
            ></ui-select>
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      ui-select {
        display: block;
        width: 100%;
        :host::ng-deep .skz-select-trigger.compact {
          display: flex;
          width: 100%;
        }
      }
    `,
  ],
})
export class CallDevicesComponent {
  chimeService = inject(ChimeService);
  private translate = inject(TranslateService);

  audioQualityOptions = computed<SelectOption[]>(() => [
    { value: 'standard', label: this.translate.instant('VideoCall.AudioQuality.Standard') },
    { value: 'high-speech', label: this.translate.instant('VideoCall.AudioQuality.HighSpeech') },
    { value: 'high-music', label: this.translate.instant('VideoCall.AudioQuality.HighMusic') },
  ]);

  videoQualityOptions = computed<SelectOption[]>(() => [
    { value: 'standard', label: this.translate.instant('VideoCall.VideoQuality.Standard') },
    { value: 'hd', label: this.translate.instant('VideoCall.VideoQuality.HD') },
    { value: 'fhd', label: this.translate.instant('VideoCall.VideoQuality.FHD') },
  ]);

  onCameraSelect(deviceId: string): void {
    this.chimeService.selectVideoInput(deviceId);
  }

  onMicrophoneSelect(deviceId: string): void {
    this.chimeService.selectAudioInput(deviceId);
  }

  onSpeakerSelect(deviceId: string): void {
    this.chimeService.selectAudioOutput(deviceId);
  }

  onAudioQualityChange(quality: 'standard' | 'high-speech' | 'high-music'): void {
    this.chimeService.setAudioQuality(quality);
  }

  onVideoQualityChange(quality: 'standard' | 'hd' | 'fhd'): void {
    this.chimeService.setVideoQuality(quality);
  }

  onEchoCancellationChange(enabled: boolean): void {
    this.chimeService.setEchoCancellation(enabled);
  }

  onNoiseSuppressionChange(enabled: boolean): void {
    this.chimeService.setNoiseSuppression(enabled);
  }

  onAutoGainControlChange(enabled: boolean): void {
    this.chimeService.setAutoGainControl(enabled);
  }

  blurStrengthOptions = computed<SelectOption[]>(() => [
    { value: 'low', label: this.translate.instant('VideoCall.BlurStrength.Low') },
    { value: 'medium', label: this.translate.instant('VideoCall.BlurStrength.Medium') },
    { value: 'high', label: this.translate.instant('VideoCall.BlurStrength.High') },
  ]);

  onBlurToggleChange(enabled: boolean): void {
    this.chimeService.setBlurPreference(enabled);
  }

  onBlurStrengthChange(strength: 'low' | 'medium' | 'high'): void {
    this.chimeService.setBlurStrength(strength);
  }
}
